// /administracion/checklists — Administración · Checklists (handoff
// autogestión 2026-10-29 §5, plan 2026-10-30 Fase 3, T-ljhq).
//
// Esta ronda hace sólo la parte de lectura + asignar sobre lo que ya
// existe en /checklist-templates (ADR-0079): a la izquierda la lista de
// plantillas con «{n} ítems · {frecuencia} · {alcance}», a la derecha la
// plantilla seleccionada con sus bloques e ítems (evidencia mínima y
// severidad sugerida como pills de solo lectura) y la acción «Asignar
// tiendas» (drawer). La selección vive en la URL (?plantilla=<id>) para
// que un enlace pegado abra la misma.
//
// Lo que el handoff dibuja y aquí no aparece, porque el backend aún no lo
// tiene y esta ronda no migra ni crea rutas nuevas (se oculta, no se
// simula): «Nueva plantilla», «Publicar cambios» y la pill Publicada /
// Borrador (no hay POST ni versiones), la edición inline de bloques e
// ítems y «Agregar ítem», los selects de evidencia y severidad (se
// muestran como pills con las etiquetas del enum y la receta actuales), y
// la fila de programación (Día de ejecución, Plazo). Quedan para la Fase 3.
//
// Quién la ve: quien administra la organización según la receta
// (canManageChecklistTemplates = canAdministerOrg) y sólo si la receta
// configura checklists por plantilla (isChecklistTemplateAdminEnabled):
// GET /checklist-templates responde 403 a los demás, así que no hay
// variante de solo lectura. La guarda de ruta la monta App.tsx; aquí sólo
// se apaga la query para un rol que no administra, así un montaje sin
// guarda nunca dispara un 403.

import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import type { ChecklistTemplateAdminSummary, RecipeMeResponse, Role } from "@bitacorapro/api-types";
import { EmptyState } from "../../components/EmptyState";
import { EvidenceRequirementPill } from "../../components/EvidenceRequirementPill";
import { Icon } from "../../components/icons";
import { PageHeader } from "../../components/PageHeader";
import { Panel } from "../../components/Panel";
import { QueryBoundary } from "../../components/QueryBoundary";
import { SkelTable } from "../../components/skeleton/SkelTable";
import type { StorePickerItem } from "../../components/StorePicker";
import { AsignarTiendasDrawer } from "../../features/administracion/checklists/AsignarTiendasDrawer";
import {
  checklistWords,
  frequencyLabel,
  pluralize,
  resolveSelectedId,
  scopeLabel,
  severityPill,
  sortTemplates,
  templateMeta,
  type ChecklistWords,
} from "../../features/administracion/checklists/model";
import {
  useChecklistTemplateDetail,
  useChecklistTemplates,
} from "../../features/administracion/checklists/queries";
import { adminWords } from "../../lib/admin/words";
import { useFacilityDashboard, useRecipe } from "../../lib/api/queries";
import { useSession } from "../../lib/auth/useSession";
import {
  canManageChecklistTemplates,
  isChecklistTemplateAdminEnabled,
  isFacilityDashboardEnabled,
} from "../../lib/capabilities";
import { useQueryView } from "../../lib/loading/useQueryView";
import { ADMIN_BASE_PATH } from "../../lib/nav";
import { effectiveBrandId } from "../../lib/util";
import { ADMIN_TOAST_OPTS, useToast } from "../../lib/useToast";

export const CHECKLISTS_PATH = `${ADMIN_BASE_PATH}/checklists`;

/** El parámetro de la URL con la plantilla seleccionada. */
export const TEMPLATE_PARAM = "plantilla";

const LIST_HEADERS = ["Plantillas"];
const DETAIL_HEADERS = ["Ítem", "Evidencia mínima", "Severidad sugerida"];

export default function Checklists() {
  const session = useSession();
  const role: Role | undefined = session?.organization.role;
  const recipeQuery = useRecipe();
  const recipe = recipeQuery.data;
  // Quién administra lo dice la receta (memberRoles[].orgAdmin) más el
  // administrador de plataforma, nunca el slug; y sólo si la receta
  // configura checklists por plantilla. Con la receta aún cargando la
  // query espera (waitingOn), no se apaga.
  const canManage =
    isChecklistTemplateAdminEnabled(recipe) &&
    canManageChecklistTemplates(recipe, role, {
      platformAdmin: session?.user.platformAdmin === true,
    });
  const words = checklistWords(recipe);
  const module = adminWords(recipe).module;
  const title = adminWords(recipe).sections.checklists;
  const toast = useToast();

  const list = useChecklistTemplates(canManage);
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedId = searchParams.get(TEMPLATE_PARAM);
  const templates = useMemo(() => sortTemplates(list.data?.templates ?? []), [list.data]);
  const selectedId = resolveSelectedId(templates, requestedId);
  const selected = templates.find((t) => t.id === selectedId) ?? null;

  function select(id: string) {
    const next = new URLSearchParams(searchParams);
    next.set(TEMPLATE_PARAM, id);
    setSearchParams(next, { replace: true });
  }

  // Candidatas del picker: las tiendas del dashboard de facility, con
  // marca efectiva (propia o de su zona), la misma fuente que Mantenciones
  // y ChecklistTemplateAssignDrawer. Apagado si la receta no lo tiene. El
  // nombre de la marca no se pinta: el handoff dice «Toda la marca» sin
  // nombrarla, así que no hace falta GET /organizations/me.
  const facilityEnabled = isFacilityDashboardEnabled(recipe);
  const dashboard = useFacilityDashboard(facilityEnabled);
  const dashboardView = useQueryView(dashboard);
  const groups = useMemo(() => dashboard.data?.byAssetGroup ?? [], [dashboard.data]);
  const groupNameById = useMemo(() => new Map(groups.map((g) => [g.id, g.name])), [groups]);
  const storesWithBrand = useMemo(
    () =>
      (dashboard.data?.byAsset ?? []).map((a) => ({
        item: {
          id: a.id,
          unit: a.unit,
          address: a.address,
          assetGroupName: a.assetGroupId ? (groupNameById.get(a.assetGroupId) ?? null) : null,
        } satisfies StorePickerItem,
        brandId: effectiveBrandId(a, groups),
      })),
    [dashboard.data, groups, groupNameById]
  );
  function storesFor(brandId: string | null): StorePickerItem[] {
    return storesWithBrand
      .filter((s) => brandId === null || s.brandId === brandId)
      .map((s) => s.item);
  }
  // null mientras no hay datos (carga o error): el toast de «Toda la
  // marca» no cuenta tiendas que no vio.
  function storeCountFor(brandId: string | null): number | null {
    if (!dashboard.data) return null;
    return storesFor(brandId).length;
  }

  const [assigning, setAssigning] = useState<ChecklistTemplateAdminSummary | null>(null);

  function saved(message: string) {
    toast.show(message, "ok", ADMIN_TOAST_OPTS);
  }

  const storesLoading = facilityEnabled && dashboardView.kind === "loading";
  const storesRetry = dashboardView.kind === "error" ? dashboardView.retry : null;

  // Receta cargada y sin permiso: App.tsx ya redirige; aquí no hay nada
  // que mostrar y la query quedó apagada.
  if (recipe !== undefined && !canManage) return null;

  return (
    <div className="page">
      <PageHeader
        eyebrow={module}
        title={title}
        sub={`Crea checklists, edita sus ítems y criterios, y asígnalos a ${words.assetPluralLower}.`}
      />

      <QueryBoundary
        query={list}
        waitingOn={[recipeQuery]}
        label="los checklists"
        skeleton={
          <div className="grid-32">
            <Panel flush>
              <SkelTable rows={4} cols={1} headers={LIST_HEADERS} />
            </Panel>
            <Panel flush>
              <SkelTable rows={6} cols={3} headers={DETAIL_HEADERS} />
            </Panel>
          </div>
        }
        isEmpty={(data) => data.templates.length === 0}
        empty={
          <Panel>
            <EmptyState
              title="Sin checklists"
              description="BitácoraPro carga las plantillas de tu empresa."
            />
          </Panel>
        }
      >
        {() => (
          <div className="grid-32">
            <Panel title="Plantillas" flush>
              <ul className="checklist-template-list">
                {templates.map((t) => {
                  const isSelected = t.id === selectedId;
                  return (
                    <li key={t.id}>
                      <button
                        type="button"
                        className={
                          isSelected
                            ? "checklist-template-row ag-row--sel"
                            : "checklist-template-row"
                        }
                        aria-current={isSelected ? "true" : undefined}
                        title={`Ver los ítems de «${t.label}»`}
                        onClick={() => select(t.id)}
                      >
                        <span className="checklist-template-row__main">
                          <span className="checklist-template-row__label">
                            {t.label}
                            {!t.active && (
                              <>
                                {" "}
                                <span className="st-pill st-pill--idle">Inactiva</span>
                              </>
                            )}
                          </span>
                          <span className="checklist-template-row__meta">
                            {templateMeta(t, words)}
                          </span>
                        </span>
                        <Icon.ChevronRight />
                      </button>
                    </li>
                  );
                })}
              </ul>
            </Panel>

            {selected && (
              <TemplateDetail
                template={selected}
                words={words}
                severities={recipe?.severities ?? []}
                onAssign={() => setAssigning(selected)}
              />
            )}
          </div>
        )}
      </QueryBoundary>

      {assigning && (
        <AsignarTiendasDrawer
          template={assigning}
          words={words}
          stores={storesFor(assigning.brandId)}
          storesLoading={storesLoading}
          storesSkelVisible={dashboardView.kind === "loading" && dashboardView.visible}
          storesRetry={storesRetry}
          storeCount={storeCountFor(assigning.brandId)}
          onClose={() => setAssigning(null)}
          onSaved={saved}
        />
      )}
    </div>
  );
}

// El panel derecho: cabecera (nombre · Asignar), ag-tpl-meta (Frecuencia
// · Alcance), los bloques con sus ítems y el pie con la leyenda. Sin la
// fila ag-sched ni la pill Publicada/Borrador: no hay programación ni
// versiones detrás todavía.
function TemplateDetail({
  template,
  words,
  severities,
  onAssign,
}: {
  template: ChecklistTemplateAdminSummary;
  words: ChecklistWords;
  severities: RecipeMeResponse["severities"];
  onAssign: () => void;
}) {
  const detail = useChecklistTemplateDetail(template.id);
  return (
    <Panel
      title={template.label}
      flush
      actions={
        <button
          type="button"
          className="btn btn--ghost btn--sm"
          title={`Elegir en qué ${words.assetPluralLower} se usa este checklist`}
          onClick={onAssign}
        >
          Asignar {words.assetPluralLower}
        </button>
      }
      foot="Evidencia mínima: lo que se exige para cerrar el ítem; siempre se puede subir más. Los cambios publicados no alteran inspecciones ya cerradas."
    >
      <div className="ag-tpl-meta">
        <div className="field ag-field">
          <span className="field__label">Frecuencia</span>
          <span className="ag-strong">{frequencyLabel(template.cadenceDays)}</span>
        </div>
        <div className="field ag-field">
          <span className="field__label">Alcance</span>
          <span className="ag-strong">{scopeLabel(template, words)}</span>
        </div>
      </div>

      {/* Una sección dentro de la página: sin anuncio propio y un error
          que no mueve el foco. */}
      <QueryBoundary
        query={detail}
        label="los ítems del checklist"
        kind="section"
        skeleton={<SkelTable rows={6} cols={3} headers={DETAIL_HEADERS} />}
        isEmpty={(data) => data.areas.every((a) => a.items.length === 0)}
        empty={
          <EmptyState
            title="Sin ítems"
            description="Este checklist todavía no tiene ítems que revisar."
          />
        }
      >
        {(data) => (
          <div className="ag-blocks">
            {/* Las cabeceras se leen como en el prototipo (sin ocultarlas);
                cada pill lleva además su columna en un prefijo sólo para
                lectores de pantalla, porque las filas no son una tabla. */}
            <div className="ag-items-head">
              <span></span>
              <span>Ítem</span>
              <span>Evidencia mínima</span>
              <span>Severidad sugerida</span>
            </div>
            {data.areas.map((area) => (
              <section
                key={area.id}
                className="ag-block"
                aria-label={`${area.label} · ${pluralize(area.items.length, "ítem", "ítems")}`}
              >
                <div className="ag-block__head">
                  <span className="ag-strong">{area.label}</span>
                  <span className="cell-meta ag-block__count">
                    {pluralize(area.items.length, "ítem", "ítems")}
                  </span>
                </div>
                {area.items.map((item) => {
                  const sev = severityPill(item.defaultSeverityKey, severities);
                  return (
                    <div key={item.id} className="ag-item">
                      <span aria-hidden="true"></span>
                      <span>
                        {item.label}
                        {item.isRequired && <span className="cell-meta"> · Obligatorio</span>}
                      </span>
                      <span>
                        <span className="sr-only">Evidencia mínima: </span>
                        <EvidenceRequirementPill
                          evidenceRequirement={item.evidenceRequirement ?? "none"}
                        />
                      </span>
                      <span>
                        <span className="sr-only">Severidad sugerida: </span>
                        {sev ? (
                          <span className={sev.tone ? `st-pill st-pill--${sev.tone}` : "st-pill"}>
                            {sev.label}
                          </span>
                        ) : (
                          <>
                            <span className="cell-meta" aria-hidden="true">
                              —
                            </span>
                            <span className="sr-only">ninguna</span>
                          </>
                        )}
                      </span>
                    </div>
                  );
                })}
              </section>
            ))}
          </div>
        )}
      </QueryBoundary>
    </Panel>
  );
}
